import React from 'react'
import { BookOpen, Heart, Clock, Users, ChefHat, Utensils } from 'lucide-react'

interface RecipeLandingPageProps {
  onGetStarted: () => void
}

export default function RecipeLandingPage({ onGetStarted }: RecipeLandingPageProps) {
  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <img src="/logo.svg" alt="Koru Kids" className="h-10" />
        </div>
      </header>

      {/* Hero Section */}
      <main className="max-w-6xl mx-auto px-4 py-16">
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Family Cookbook
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
            The easiest way to give your nanny clear, personalised meal ideas for your children.
          </p>
          <button
            onClick={onGetStarted}
            className="inline-flex items-center gap-2 bg-emerald-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-emerald-700 transition-colors shadow-lg shadow-emerald-200"
          >
            <BookOpen className="w-5 h-5" />
            Create Your Cookbook
          </button>
        </div>

        {/* What it does */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 mb-12">
          <p className="text-gray-600 text-lg">
            This tool turns your family's food preferences, allergies, routines, and nutritional goals into a personalised cookbook for your nanny.
          </p>
        </div>

        {/* Features */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          <FeatureCard
            icon={<Heart className="w-6 h-6 text-emerald-600" />}
            title="Tailored to your children"
            description="Recipes based on what your children actually like to eat"
          />
          <FeatureCard
            icon={<ChefHat className="w-6 h-6 text-emerald-600" />}
            title="Allergy-aware"
            description="Every recipe screened against your family's allergies"
          />
          <FeatureCard
            icon={<Utensils className="w-6 h-6 text-emerald-600" />}
            title="Nutritional goals"
            description="Your priorities woven into every recipe choice"
          />
          <FeatureCard
            icon={<Clock className="w-6 h-6 text-emerald-600" />}
            title="Quick & practical"
            description="Recipes designed for busy weekday evenings"
          />
          <FeatureCard
            icon={<Users className="w-6 h-6 text-emerald-600" />}
            title="Clear for nannies"
            description="Tips, timings, and photos for confident cooking"
          />
          <FeatureCard
            icon={<BookOpen className="w-6 h-6 text-emerald-600" />}
            title="Complete cookbook"
            description="15 recipes, a meal plan, and emergency fallbacks"
          />
        </div>

        {/* How to use */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">How to use it</h2>
          <div className="grid md:grid-cols-4 gap-6">
            <Step number={1} text="Answer a few questions" />
            <Step number={2} text="Add any routines or restrictions" />
            <Step number={3} text="Receive your personalised cookbook by email" />
            <Step number={4} text="Share it with your nanny" />
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <button
            onClick={onGetStarted}
            className="inline-flex items-center gap-2 bg-emerald-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-emerald-700 transition-colors shadow-lg shadow-emerald-200"
          >
            <BookOpen className="w-5 h-5" />
            Get Started
          </button>
          <p className="text-gray-500 mt-4 text-sm">
            Takes about 5-10 minutes to complete
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-8 mt-16">
        <div className="max-w-6xl mx-auto px-4 text-center text-gray-500 text-sm">
          <p>Made by Koru Kids - the specialist nanny agency for families</p>
          <p className="mt-2">www.korukids.co.uk</p>
        </div>
      </footer>
    </div>
  )
}

function FeatureCard({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="mb-4">{icon}</div>
      <h3 className="font-semibold text-gray-900 mb-2">{title}</h3>
      <p className="text-gray-600 text-sm">{description}</p>
    </div>
  )
}

function Step({ number, text }: { number: number; text: string }) {
  return (
    <div className="text-center">
      <div className="w-10 h-10 bg-emerald-600 text-white rounded-full flex items-center justify-center font-bold text-lg mx-auto mb-3">
        {number}
      </div>
      <p className="text-gray-600">{text}</p>
    </div>
  )
}
